import React, { FC, useEffect, useState } from 'react'
import { Flex, WPGSelect } from '@ui'
import cx from 'classnames'
import css from './aboutTab.module.scss'
import { EvoGhostButton, EvoButton } from '@ui/buttons/EvoButton'
import floorPlanCss from '../../adminFloorPlan.module.scss'
import SelectWrapper from '../SelectWrapper/SelectWrapper'
import { useFloor } from '../../../../providers/mapProvider'
import get from 'lodash/get'
import useSpaceDetails from '../../hooks/useSpaceDetails'
import api, { ICalendar, ISpaceType, ITag, IWorkplaceGroup } from '@api'
import UsageWarningDialog, { getWarningMessage } from './UsageWarningDialog'
import { seatable } from '@ui/canvas/utils'
import EvoTreeSelect from 'components/EvoTreeSelect'

import SpaceTypeEvoSelect from '@ui/selects/SpaceTypeEvoSelect'
import UsageTypeEvoSelect from '@ui/selects/LocationSelect/UsageTypeEvoSelect'

import MeasurementInput from '../MeasurementInput'

import CalendarSelect from '@ui/selects/CalendarSelect'
import { isMeetingRoom } from '@utils'

import { Label, TextField } from '@runeffective/evo-design-system'
import MultiAssetsSelect from '@ui/selects/MultiAssetsSelect'

const AboutTab = ({
  canChangeCalendar,
  setSplitScreen,
}: {
  canChangeCalendar: boolean
  setSplitScreen: (value: boolean) => void
}) => {
  const floor = useFloor()
  const selectedSpaceId = get(floor, 'selection.selectedSpacesList[0]', '')
  const {
    data: { space, isDirty },
    updateData,
    resetData,
    update,
  } = useSpaceDetails(selectedSpaceId)
  const [usageAssign, setUsageAssign] = useState(false)
  const [value, setValue] = useState(0)
  const [updatedUsageType, setUpdatedUsageType] = useState(null)

  return (
    <>
      <div>
        <div className={floorPlanCss.tab_row}>
          <SelectWrapper title="Department allocation">
            <EvoTreeSelect
              value={space?.department || null}
              onChange={(department) => {
                updateData({ department })
              }}
            />
          </SelectWrapper>
          <p className={floorPlanCss.tab_description}>
            Changing the department for the selected seats will unseat any seated employees that are
            part of this selection
          </p>
        </div>
        <div className={floorPlanCss.tab_row}>
          <SelectWrapper title="Space type">
            <SpaceTypeEvoSelect
              value={space?.spaceType || null}
              onChange={(spaceType) => {
                updateData({ spaceType })
              }}
            />
          </SelectWrapper>
        </div>
        {seatable(space?.spaceType) && (
          <div className={floorPlanCss.tab_row}>
            <SelectWrapper title="Usage type">
              <UsageTypeEvoSelect
                value={space?.usageType}
                filterable={false}
                onChange={(usageType) => {
                  updateData({ usageType })
                }}
              />
            </SelectWrapper>
          </div>
        )}
        {isMeetingRoom(space?.spaceType as ISpaceType | null) && (
          <div className={floorPlanCss.tab_row}>
            <SelectWrapper title="Select calendar">
              <CalendarSelect
                disabled={!canChangeCalendar}
                value={space?.calendar as ICalendar | null}
                onChange={(calendar: any) => {
                  updateData({ calendar })
                }}
              />
            </SelectWrapper>
          </div>
        )}
        <div className={floorPlanCss.tab_row}>
          <MeasurementInput
            value={space?.measurements as number}
            onChange={(measurements) => {
              updateData({ measurements: measurements.target.value })
            }}
          />
        </div>

        <div className={floorPlanCss.tab_row}>
          <Label label="Label" />
          <TextField
            style={{ height: '32px' }}
            placeholder="Label"
            value={space?.name}
            onChange={(name) => {
              updateData({ name: name })
            }}
          />
        </div>
        <div className={floorPlanCss.tab_row}>
          <Label label="Sensors" />
          <TextField
            style={{ height: '32px' }}
            placeholder="Sensors"
            value={space?.comment}
            onChange={(comment) => {
              updateData({ comment: comment })
            }}
          />
        </div>

        <div className={floorPlanCss.tab_row}>
          <Label label="Space Asset" />
          <MultiAssetsSelect
            values={space?.tags}
            onCreate={(name) => {
              api.asset.create(name).then((tag) => {
                updateData({
                  tags: [...(space?.tags as []), tag],
                })
              })
            }}
            onChange={(tags) => {
              updateData({ tags })
            }}
            onClear={() => {
              updateData({
                tags: [],
              })
            }}
          />
        </div>

        <div className={floorPlanCss.tab_row}>
          <SelectWrapper title="Capacity">
            <TextField
              size="small"
              value={space?.capacity.toString()}
              onChange={(capacity) => {
                updateData({ capacity: Number(capacity) })
              }}
            />
          </SelectWrapper>
        </div>

        <div className={floorPlanCss.tab_row}>
          <SelectWrapper title="Workplace Group">
            <WPGSelect
              value={space?.workplaceGroup || null}
              filterable={true}
              onCreate={(name) => {
                api.workplaceGroup.create({ name }).then((workplaceGroup) => {
                  updateData({ workplaceGroup })
                })
              }}
              canCreate={true}
              buttonProps={{ style: { width: 270 } }}
              onChange={(workplaceGroup) => {
                updateData({ workplaceGroup })
              }}
            />
          </SelectWrapper>
        </div>
        <Flex justify="stretch" className={cx(css.footer, floorPlanCss.tab_row)}>
          <EvoGhostButton
            onClick={() => {
              resetData()
              floor?.selection?.clear()
              setSplitScreen(false)
            }}>
            Cancel
          </EvoGhostButton>
          <EvoButton
            disabled={!isDirty}
            onClick={() => {
              update(floor.apId)
            }}>
            Update
          </EvoButton>
        </Flex>
      </div>
      <UsageWarningDialog
        spaceDetails={{
          employee: space?.employee,
          oldType: space?.usageType?.name,
          newType: updatedUsageType,
          bookings: space?.bookings,
        }}
        usageAssign={usageAssign}
        setUsageAssign={() => setUsageAssign(false)}
        assignUsageType={() => {
          updateData({ usageType: updatedUsageType })
          setUsageAssign(false)
        }}
        cancelAssignment={() => {
          setUsageAssign(false)
          setUpdatedUsageType(null)
        }}
      />
    </>
  )
}

export default AboutTab
